/**
 * Shipturtle API Service
 * Handles all API calls for Shipturtle products, orders, and related functionality
 */

import { handleRawFetch401 } from '../utils/apiInterceptor';
import { readErrorBody } from '../utils/readErrorBody';
import { classifyError } from '../utils/errorTaxonomy';
import {
  resilientFetch,
  TIMEOUT_NORMAL,
  TIMEOUT_SLOW,
} from '../utils/resilientFetch';

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;
const API_V2_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/shipturtle/v2`;

/**
 * The transport every call in this module goes through.
 *
 * Previously each of these was a bare `fetch` with no deadline, so a request the
 * server never answered — which is every request for the 30-120s a deploy takes —
 * left the UI spinning indefinitely with nothing to report.
 *
 * Reads retry with jittered backoff. Writes get a deadline but are not replayed:
 * neither a timeout nor a 502 proves the server rejected them, and a duplicated
 * order is worse than a message asking the user to try again.
 *
 * `fetchImpl` is plain `fetch` rather than `authenticatedFetch` because this
 * module carries its own `getAuthHeaders()` and routes 401s through
 * `handleRawFetch401`. Swapping that is a separate change; layering both would
 * put two competing refresh paths on the same request.
 */
const stFetch = (url, options = {}, resilience = {}) =>
  resilientFetch(url, options, {
    timeoutMs: TIMEOUT_NORMAL,
    fetchImpl: (u, o) => fetch(u, o),
    ...resilience,
  });

/**
 * For endpoints that build a CSV or call Shopify inline and legitimately need
 * most of a minute. No retry: they are expensive, and a second attempt would
 * more likely finish off the worker pool than succeed.
 */
const stFetchSlow = (url, options = {}) =>
  stFetch(url, options, { timeoutMs: TIMEOUT_SLOW, retries: 0 });

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

// Helper for multipart requests (FormData). Do NOT set Content-Type so browser can set boundary.
const getAuthHeadersMultipart = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
  return {
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

const isFileLike = (v) => {
  // File/Blob exist in browser context; guard for SSR/tests
  return (
    (typeof File !== 'undefined' && v instanceof File) ||
    (typeof Blob !== 'undefined' && v instanceof Blob)
  );
};

const buildVendorUpdateFormData = (vendorData = {}) => {
  const formData = new FormData();

  const appendIfDefined = (key, value) => {
    if (value === undefined || value === null) return;
    // Backend expects strings for most multipart fields
    if (typeof value === 'boolean') {
      formData.append(key, value ? 'true' : 'false');
      return;
    }
    if (typeof value === 'object' && !isFileLike(value)) {
      // Nested objects must be serialized by caller; special-case vendor_settings below.
      formData.append(key, JSON.stringify(value));
      return;
    }
    formData.append(key, String(value));
  };

  // Scalar fields (match backend keys)
  appendIfDefined('company_name', vendorData.company_name);
  // No `?? ''` here: tally_name drives the CRVW_ Tally ledger, and sending a blank
  // for an absent value silently wipes it on every unrelated vendor save.
  appendIfDefined('tally_name', vendorData.tally_name);
  appendIfDefined('brand_name', vendorData.brand_name);
  appendIfDefined('website', vendorData.website ?? '');
  appendIfDefined('primary_contact_name', vendorData.primary_contact_name);
  appendIfDefined('primary_contact_phone', vendorData.primary_contact_phone);
  appendIfDefined('status', vendorData.status);
  appendIfDefined('public_description', vendorData.public_description ?? '');

  // Vendor settings (backend expects a JSON string in multipart)
  if (vendorData.vendor_settings !== undefined) {
    const vs = vendorData.vendor_settings;
    // If it's already a string, keep it.
    if (typeof vs === 'string') formData.append('vendor_settings', vs);
    else formData.append('vendor_settings', JSON.stringify(vs || {}));
  }

  // Compatibility: some backends also accept these at top-level
  appendIfDefined('facebook_link', vendorData.facebook_link);
  appendIfDefined('instagram_link', vendorData.instagram_link);
  appendIfDefined('tiktok_link', vendorData.tiktok_link);
  appendIfDefined('twitter_link', vendorData.twitter_link);
  appendIfDefined('hide_promotion_banner', vendorData.hide_promotion_banner);

  // Files
  if (isFileLike(vendorData.logo)) formData.append('logo', vendorData.logo);
  if (isFileLike(vendorData.document)) formData.append('document', vendorData.document);
  if (isFileLike(vendorData.promotion_banner)) formData.append('promotion_banner', vendorData.promotion_banner);

  return formData;
};

/**
 * Shopify Sync Field Filters API
 */
export const getSyncFieldFilters = async (vendorId) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/vendors/${vendorId}/sync-field-filters/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const updateSyncFieldFilters = async (vendorId, filters, additionalSettings = {}, settingsType = 'product') => {
  const nestedKey = settingsType === 'variant' ? 'variant_sync_settings' 
    : settingsType === 'order' ? 'order_sync_settings'
    : 'product_sync_settings';
  
  const payload = { 
    [nestedKey]: filters,
    ...additionalSettings
  };
  
  const payloadString = JSON.stringify(payload);
  console.log("DEBUG: updateSyncFieldFilters EXACT payload being sent:", payloadString);

  const response = await stFetch(`${API_BASE_URL}/shipturtle/vendors/${vendorId}/sync-field-filters/`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: payloadString,
  });

  if (!response.ok) {
    const errorBody = await response.text();
    console.error("DEBUG: updateSyncFieldFilters PATCH error response:", errorBody);
    
    let errorData = {};
    try {
      errorData = JSON.parse(errorBody);
    } catch (e) {
      errorData = { error: errorBody };
    }
    
    throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
  }

  const successData = await response.json().catch(() => ({}));
  console.log("DEBUG: updateSyncFieldFilters SUCCESS RESPONSE:", successData);
  return successData;
};

/**
 * Turn a Response into data, or throw something a caller can actually describe.
 *
 * The thrown Error carries `status`, `data` and `classified` alongside the
 * message. It used to carry only the message, which is why so much code
 * downstream resorted to string-matching and why users saw "HTTP 502": the
 * status was read and then discarded right here.
 *
 * `error.message` stays human-readable so the many existing `catch` blocks that
 * render it directly improve rather than break, but anything new should prefer
 * `error.classified` — it already holds the title, body copy and whether a retry
 * is worth offering.
 */
const handleResponse = async (response) => {
  // Gracefully handle 204 No Content and other empty-body success responses
  if (response.status === 204) {
    return {};
  }

  if (!response.ok) {
    // Handle 401 errors globally
    if (response.status === 401) {
      console.log('[shipturtleApi] 401 detected - triggering session expired popup');
      handleRawFetch401();
      throw new Error('Session expired');
    }

    // Never `response.json()` blindly: an uncaught Django exception comes back
    // as text/plain and nginx's 502/503 pages are HTML, so parsing throws on
    // exactly the failures we most need to explain.
    const { body } = await readErrorBody(response);
    const error = body || {};

    // A serializer refusal arrives as `errors` ({field: [messages]}, nested
    // for addresses and line items) with no `error` string; without this the
    // operator saw only "HTTP error! status: 400".
    const firstSerializerMessage = (value) => {
      if (!value) return '';
      if (typeof value === 'string') return value;
      if (Array.isArray(value)) {
        for (const item of value) {
          const found = firstSerializerMessage(item);
          if (found) return found;
        }
        return '';
      }
      if (typeof value === 'object') {
        for (const item of Object.values(value)) {
          const found = firstSerializerMessage(item);
          if (found) return found;
        }
      }
      return '';
    };

    const classified = classifyError(
      Object.assign(new Error(`HTTP ${response.status}`), {
        status: response.status,
        data: body,
      }),
      { response, body }
    );

    // The message stays factual — the backend's own sentence, the first
    // serializer complaint, or the status. Substituting the reassuring copy
    // here once put outage wording into alerts about unrelated write failures,
    // which reached production; callers that want it read `classified.message`.
    const thrown = new Error(
      error.error ||
        error.message ||
        error.detail ||
        firstSerializerMessage(error.errors) ||
        `HTTP error! status: ${response.status}`
    );
    // The structured body, for forms that mark each field rather than show one
    // sentence: `errors` (serializer) and `field_errors` (label validation).
    thrown.status = response.status;
    thrown.data = error;
    thrown.errors = error.errors;
    thrown.fieldErrors = error.field_errors;
    thrown.classified = classified;
    throw thrown;
  }

  // For successful responses, there might still be no body (e.g. 202/201 without JSON)
  try {
    return await response.json();
  } catch {
    return {};
  }
};

// Normalizes the single-or-bulk productIds argument accepted by the catalog-action
// helpers (delete, moveToDraft, moveToActive, moveToUnlisted) into the product_ids
// list the backend always expects. Returns null for anything else so callers can
// throw a consistent error.
const _asIdList = (productIds) => {
  if (typeof productIds === 'string') return [productIds];
  if (Array.isArray(productIds) && productIds.length > 0) return productIds;
  return null;
};

// Builds the request body for a bulk product action, in one of two shapes:
//
//   { product_ids: [...] }                                   explicit picks
//   { select_all: true, filters: {...}, excluded_ids: [...] } everything matching
//
// In select-all mode the id list is never sent — the backend re-resolves the set
// from the same filters the list view used, minus anything the user unticked.
// Returns null when neither mode yields a usable selection, so callers can throw a
// consistent error.
const _buildSelectionBody = (productIds, selection = null) => {
  if (selection?.selectAll) {
    return {
      select_all: true,
      filters: selection.filters || {},
      excluded_ids: selection.excludedIds || [],
    };
  }
  const ids = _asIdList(productIds);
  return ids ? { product_ids: ids } : null;
};

// ============================================
// USER API
// ============================================

/**
 * Get current user's Shipturtle role
 * @returns {Promise<{role: string, user_id: string, user_email: string, vendor_id: string|null, vendor_name: string|null}>}
 */
export const getCurrentUserRole = async () => {
  const response = await stFetch(`${API_V2_BASE_URL}/user/role/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};


// ============================================
// PRODUCTS API
// ============================================

/**
 * List products with filtering and pagination.
 *
 * The backend paginates on limit/offset. Callers may pass either limit/offset
 * (preferred) or page/page_size, which is converted to limit/offset before the
 * request — one of the two pairs is required.
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Page size for backend (required unless page_size is given)
 * @param {number} params.offset - Offset for backend (0-based, defaults to 0)
 * @param {number} [params.page] - 1-based page number (alternative to limit/offset)
 * @param {number} [params.page_size] - Page size (alternative to limit/offset)
 * @param {string} params.status - Filter by status (draft, pending_approval, approved, live, unlisted, deleted)
 * @param {string} params.product_state - Filter by product state (active, inactive)
 * @param {string} params.category_id - Filter by category UUID
 * @param {string} params.vendor_id - Filter by vendor UUID (admin only)
 * @param {string} params.search - Search term for title, description, vendor name
 * @param {string} params.date_from - Filter by created date (ISO format)
 * @param {string} params.date_to - Filter by created date (ISO format)
 * @returns {Promise<{products: Array, pagination: Object}>}
 */
export const listProducts = async (params = {}) => {
  const queryParams = new URLSearchParams();

  // Pagination: the products endpoint expects limit/offset only. Callers may
  // express the same thing as page/page_size, so normalize that form down to
  // limit/offset rather than passing it through — page/page_size on the wire is
  // silently ignored by the backend, which then falls back to the first 20 rows.
  let effectiveLimit, effectiveOffset;
  if (params.limit !== undefined || params.offset !== undefined) {
    effectiveLimit = Number(params.limit);
    effectiveOffset = params.offset === undefined ? 0 : Number(params.offset);
    if (!Number.isFinite(effectiveLimit) || effectiveLimit <= 0) {
      throw new Error('listProducts: "limit" is required and must be a positive number');
    }
    if (!Number.isFinite(effectiveOffset) || effectiveOffset < 0) {
      throw new Error('listProducts: "offset" must be a non-negative number');
    }
  } else {
    const pageSize = Number(params.page_size);
    const page = params.page === undefined ? 1 : Number(params.page);
    if (!Number.isFinite(pageSize) || pageSize <= 0) {
      throw new Error('listProducts: "limit" or "page_size" is required and must be a positive number');
    }
    if (!Number.isFinite(page) || page < 1) {
      throw new Error('listProducts: "page" must be a positive number');
    }
    effectiveLimit = pageSize;
    effectiveOffset = (page - 1) * pageSize;
  }

  queryParams.append('limit', String(effectiveLimit));
  queryParams.append('offset', String(effectiveOffset));

  // Copy through filters, excluding pagination keys
  const paginationKeys = ['limit', 'offset', 'page', 'page_size'];
  Object.keys(params).forEach((key) => {
    if (paginationKeys.includes(key)) return;
    const value = params[key];
    if (value !== null && value !== undefined && value !== '' && value !== 'all') {
      queryParams.append(key, value);
    }
  });

  const url = `${API_V2_BASE_URL}/products/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const response = await stFetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  const data = await handleResponse(response);

  // Backend response shape: { success, count, limit, offset, products }
  // Also tolerate a DRF-style { results: [...], count } envelope.
  const products = data.results || data.products || [];
  const totalCount = Number(
    data.count !== undefined ? data.count : data.total_count !== undefined ? data.total_count : 0
  );

  // Trust the server's echoed limit/offset over what we asked for — it clamps
  // limit to its own max page size, so the requested value can differ from the
  // page actually returned.
  const limitFromResponse = Number(data.limit !== undefined ? data.limit : effectiveLimit);
  const offsetFromResponse = Number(data.offset !== undefined ? data.offset : effectiveOffset);

  const safeLimit = Number.isFinite(limitFromResponse) && limitFromResponse > 0
    ? limitFromResponse
    : effectiveLimit;
  const safeOffset = Number.isFinite(offsetFromResponse) && offsetFromResponse >= 0
    ? offsetFromResponse
    : effectiveOffset;

  const currentPage = Math.floor(safeOffset / safeLimit) + 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / safeLimit));

  const pagination = {
    page: currentPage,
    page_size: safeLimit,
    total_pages: totalPages,
    total_count: totalCount,
    total: totalCount,
    pages: totalPages,
    // Keep these for any legacy code that reads them
    limit: safeLimit,
    offset: safeOffset,
  };

  return {
    ...data,
    products,   // normalized: always present regardless of server key name
    pagination,
  };
};

/**
 * Get detailed product information
 * @param {string} productId - Product UUID
 * @returns {Promise<{product: Object}>}
 */
export const getProductDetail = async (productId) => {
  const response = await stFetch(`${API_V2_BASE_URL}/products/${productId}/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  return handleResponse(response);
};

/**
 * Create a new product
 * @param {Object} productData - Product data
 * @returns {Promise<{product: Object}>}
 */
export const createProduct = async (productData) => {
  const response = await stFetch(`${API_V2_BASE_URL}/products/create/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(productData),
  });

  return handleResponse(response);
};

/**
 * Update an existing product
 * @param {string} productId - Product UUID
 * @param {Object} productData - Updated product data
 * @returns {Promise<{product: Object}>}
 */
export const updateProduct = async (productId, productData) => {
  const response = await stFetch(`${API_V2_BASE_URL}/products/${productId}/update/`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(productData),
  });

  return handleResponse(response);
};

/**
 * Update product options (names and values)
 * @param {string} productId - Product UUID
 * @param {Object} data - { options: Array, reason: string }
 */
export const updateProductOptions = async (productId, data) => {
  const response = await stFetch(`${API_V2_BASE_URL}/products/${productId}/options/`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  return handleResponse(response);
};

/**
 * Inline edit a single variant field
 * @param {string} productId - Product UUID
 * @param {string} variantId - Variant UUID
 * @param {Object} data - { [field]: value, reason: string }
 */
export const updateVariantField = async (productId, variantId, data) => {
  const response = await stFetch(`${API_V2_BASE_URL}/products/${productId}/variants/${variantId}/`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  return handleResponse(response);
};

/**
 * Assign an image to a variant
 * @param {string} productId - Product UUID
 * @param {string} variantId - Variant UUID
 * @param {Object} data - { image_id: string|null }
 */
export const assignVariantImage = async (productId, variantId, data) => {
  const response = await stFetch(`${API_V2_BASE_URL}/products/${productId}/variants/${variantId}/image/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  return handleResponse(response);
};

/**
 * Reorder product images
 * @param {string} productId - Product UUID
 * @param {Object} data - { media: [{id: string, position: number}, ...] }
 */
export const reorderProductImages = async (productId, data) => {
  const response = await stFetch(`${API_V2_BASE_URL}/products/${productId}/media/reorder/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  return handleResponse(response);
};

/**
 * Delete a product (soft delete)
 * @param {string} productId - Product UUID
 * @param {string} reason - Reason for deletion (required for vendors)
 * @returns {Promise<{message: string}>}
 */
/**
 * Delete product(s) - unified endpoint for single or bulk operations
 * @param {string|Array<string>} productIds - Single product UUID or array of UUIDs
 * @param {string} reason - Reason for deletion
 * @returns {Promise<{message: string, deleted_count: number, errors: Array}>}
 */
export const deleteProduct = async (productIds, reason = '') => {
  const ids = _asIdList(productIds);
  if (ids) {
    const response = await stFetch(`${API_V2_BASE_URL}/products/delete/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ product_ids: ids, reason }),
    });
    return handleResponse(response);
  }
  throw new Error('productIds must be a string or array');
};

// ============================================
// CATEGORIES API
// ============================================

/**
 * List all categories
 * @returns {Promise<{categories: Array}>}
 */
export const listCategories = async () => {
  const response = await stFetch(`${API_V2_BASE_URL}/categories/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  return handleResponse(response);
};

/**
 * Get category hierarchy
 * @returns {Promise<{categories: Array}>}
 */
export const getCategoryHierarchy = async () => {
  const response = await stFetch(`${API_V2_BASE_URL}/categories/hierarchy/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  return handleResponse(response);
};

/**
 * Search the global variant-option-type catalog (Size, Color, ...) by name.
 * @param {string} search - substring to filter by (case-insensitive); omit for all
 * @returns {Promise<{options: Array<{id: string, name: string}>}>}
 */
export const searchVariantOptionCatalog = async (search = '') => {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  const response = await stFetch(`${API_V2_BASE_URL}/variant-options/?${params.toString()}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  return handleResponse(response);
};

/**
 * Get-or-create a variant-option-type catalog entry (e.g. "Finger Size").
 * Case-insensitive: creating "size" when "Size" already exists returns the
 * existing "Size" entry rather than a duplicate.
 * @param {string} name
 * @returns {Promise<{id: string, name: string, created: boolean}>}
 */
export const createVariantOptionCatalogEntry = async (name) => {
  const response = await stFetch(`${API_V2_BASE_URL}/variant-options/create/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ name }),
  });

  return handleResponse(response);
};

// ============================================
// ORDERS API (placeholder for future implementation)
// ============================================

/**
 * List orders with filtering and pagination
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Page size for backend (required)
 * @param {number} params.offset - Offset for backend (required, 0-based)
 * @param {string} params.status - Filter by status (required)
 * @param {string} params.vendor_id - Filter by vendor UUID (optional, admin only)
 * @returns {Promise<{orders: Array, pagination: Object}>}
 */
export const listOrders = async (params = {}) => {
  const queryParams = new URLSearchParams();

  // Pagination: backend expects limit/offset only
  const effectiveLimit = Number(params.limit);
  const effectiveOffset = Number(params.offset);

  if (!Number.isFinite(effectiveLimit) || effectiveLimit <= 0) {
    throw new Error('listOrders: "limit" is required and must be a positive number');
  }
  if (!Number.isFinite(effectiveOffset) || effectiveOffset < 0) {
    throw new Error('listOrders: "offset" is required and must be a non-negative number');
  }

  queryParams.append('limit', String(effectiveLimit));
  queryParams.append('offset', String(effectiveOffset));

  // Copy through the rest of the filters, excluding pagination keys (limit/offset)
  Object.keys(params).forEach((key) => {
    if (['limit', 'offset'].includes(key)) {
      return;
    }
    const value = params[key];
    if (value !== null && value !== undefined && value !== '' && value !== 'all') {
      queryParams.append(key, value);
    }
  });

  const url = `${API_BASE_URL}/shipturtle/orders/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const response = await stFetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  const data = await handleResponse(response);

  // Normalize pagination metadata from the backend response
  // Backend response shape:
  // {
  //   success: true,
  //   count: number,
  //   limit: number,
  //   offset: number,
  //   orders: [...]
  // }
  const totalCount = Number(
    data.total_count !== undefined ? data.total_count : data.count !== undefined ? data.count : 0
  );
  const limitFromResponse = Number(
    data.limit !== undefined ? data.limit : effectiveLimit
  );
  const offsetFromResponse = Number(
    data.offset !== undefined ? data.offset : effectiveOffset
  );

  const safeLimit = !Number.isNaN(limitFromResponse) && limitFromResponse > 0
    ? limitFromResponse
    : effectiveLimit || 25;
  const safeOffset = !Number.isNaN(offsetFromResponse) && offsetFromResponse >= 0
    ? offsetFromResponse
    : effectiveOffset || 0;

  const currentPage = Math.floor(safeOffset / safeLimit) + 1;
  const totalPages = safeLimit > 0 ? Math.max(1, Math.ceil(totalCount / safeLimit)) : 1;

  const pagination = {
    // Legacy fields expected by existing components
    page: currentPage,
    page_size: safeLimit,
    total_pages: totalPages,
    total_count: totalCount,
    // Additional aliases for compatibility with older code paths
    total: totalCount,
    pages: totalPages,
    limit: safeLimit,
    offset: safeOffset,
  };

  return {
    ...data,
    pagination,
  };
};

/**
 * Get detailed order information
 * @param {string} orderId - Order UUID
 * @returns {Promise<{order: Object}>}
 */
export const getOrderDetail = async (orderId) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/${orderId}/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  return handleResponse(response);
};

/**
 * Approve a product (admin only)
 * @param {string} productId - Product UUID
 * @returns {Promise<{product: Object}>}
 */
export const approveProduct = async (productId, options = {}) => {
  // Backend supports an optional `mode` (e.g., 'push' or 'queue') in the request body
  const body = options && options.mode ? JSON.stringify({ mode: options.mode }) : undefined;
  const headers = body ? getAuthHeaders() : getAuthHeaders();
  const response = await stFetch(`${API_V2_BASE_URL}/products/${productId}/approve/`, {
    method: 'POST',
    headers,
    ...(body ? { body } : {}),
  });

  return handleResponse(response);
};

/**
 * Reject a product (admin only)
 * @param {string} productId - Product UUID
 * @param {string} reason - Reason for rejection
 * @returns {Promise<{product: Object}>}
 */
export const rejectProduct = async (productId, reason) => {
  const response = await stFetch(`${API_V2_BASE_URL}/products/${productId}/reject/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ reason }),
  });

  return handleResponse(response);
};

/**
 * Request changes for a product (admin only)
 * @param {string} productId - Product UUID
 * @param {string} reason - Reason for requesting changes
 * @returns {Promise<{product: Object}>}
 */
export const requestChangesProduct = async (productId, reason) => {
  const response = await stFetch(`${API_V2_BASE_URL}/products/${productId}/request-changes/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ reason }),
  });

  return handleResponse(response);
};

/**
 * Bulk approve products (admin only)
 * @param {Object|Array<string>} payload - Either an object with product_ids and mode, or array of product IDs
 * @returns {Promise<{approved_count: number, failed_count: number}>}
 */
export const bulkApproveProducts = async (payload) => {
  // Normalize payload to avoid nested structures like { product_ids: { product_ids: [...] } }
  let body;
  if (Array.isArray(payload)) {
    body = { product_ids: payload };
  } else if (payload && typeof payload === 'object') {
    // Common case: { product_ids: [...], mode?: 'queue'|'push' }
    if (Array.isArray(payload.product_ids)) {
      body = { product_ids: payload.product_ids, mode: payload.mode || 'queue' };
      // Nested case: { product_ids: { product_ids: [...], mode?: 'queue' } }
    } else if (
      payload.product_ids && typeof payload.product_ids === 'object' &&
      Array.isArray(payload.product_ids.product_ids)
    ) {
      body = {
        product_ids: payload.product_ids.product_ids,
        mode: payload.product_ids.mode || payload.mode || 'queue',
      };
    } else {
      body = payload;
    }
  } else {
    body = { product_ids: [] };
  }

  const response = await stFetch(`${API_V2_BASE_URL}/products/bulk-approve/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });

  return handleResponse(response);
};

/**
 * Move product(s) to draft status - unified endpoint for single or bulk operations
 * @param {string|Array<string>} productIds - Single product UUID or array of UUIDs
 * @param {string} reason - Reason for moving to draft
 * @returns {Promise<{message: string, moved_count: number, errors: Array}>}
 */
export const moveToDraft = async (productIds, reason = '', selection = null) => {
  const body = _buildSelectionBody(productIds, selection);
  if (!body) throw new Error('productIds must be a string or array');
  const response = await stFetch(`${API_V2_BASE_URL}/products/move-to-draft/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ...body, reason }),
  });
  return handleResponse(response);
};

/**
 * Move product(s) to active status - single or bulk.
 *
 * A Terminal-originated status change: the backend sends the product(s) to PENDING
 * approval rather than pushing to Shopify immediately (see the backend view) — the
 * change reaches the merchant store only once an admin approves it.
 *
 * @param {string|Array<string>} productIds - Single product UUID or array of UUIDs
 * @param {string} reason - Reason for moving to active
 * @returns {Promise<{message: string, moved_count: number, errors: Array}>}
 */
export const moveToActive = async (productIds, reason = '', selection = null) => {
  const body = _buildSelectionBody(productIds, selection);
  if (!body) throw new Error('productIds must be a string or array');
  const response = await stFetch(`${API_V2_BASE_URL}/products/move-to-active/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ...body, reason }),
  });
  return handleResponse(response);
};

/**
 * Move product(s) to unlisted status - single or bulk.
 *
 * Unlisted = still ACTIVE on Shopify but removed from the Online Store sales channel.
 * A Terminal-originated status change: the backend sends the product(s) to PENDING
 * approval rather than delisting on Shopify immediately — the delisting only happens
 * once an admin approves.
 *
 * @param {string|Array<string>} productIds - Single product UUID or array of UUIDs
 * @param {string} reason - Reason for unlisting
 * @returns {Promise<{message: string, moved_count: number, errors: Array}>}
 */
export const moveToUnlisted = async (productIds, reason = '', selection = null) => {
  const body = _buildSelectionBody(productIds, selection);
  if (!body) throw new Error('productIds must be a string or array');
  const response = await stFetch(`${API_V2_BASE_URL}/products/move-to-unlisted/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ...body, reason }),
  });
  return handleResponse(response);
};

/**
 * Bulk move products to draft status (deprecated - use moveToDraft instead)
 * @param {Array<string>} productIds - Array of product UUIDs (ignored when selection.selectAll)
 * @param {string} reason - Reason for moving to draft
 * @param {{selectAll: boolean, filters: Object, excludedIds: Array<string>}} [selection]
 * @returns {Promise<{moved_count: number, errors: Array}>}
 */
export const bulkMoveToDraft = async (productIds, reason = '', selection = null) => {
  return moveToDraft(productIds, reason, selection);
};

/**
 * Bulk delete products (deprecated - use deleteProduct instead)
 * @param {Array<string>} productIds - Array of product UUIDs
 * @returns {Promise<{deleted_count: number, errors: Array}>}
 */
export const bulkDeleteProducts = async (productIds) => {
  return deleteProduct(productIds);
};

/**
 * Push a single product to pending approval
 * @param {string} productId - Product UUID
 * @returns {Promise<{status: string}>}
 */
export const pushToPendingApproval = async (productId) => {
  const response = await stFetch(`${API_V2_BASE_URL}/products/${productId}/push-to-pending-approval/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({}),
  });
  return handleResponse(response);
};

/**
 * Bulk push products to pending approval status
 * @param {Array<string>} productIds - Array of product UUIDs
 * @param {string} reason - Reason for pushing (required for admin)
 * @returns {Promise<{updated_count: number, errors: Array}>}
 */
export const bulkPushToPendingApproval = async (
  productIds,
  reason = 'Product pushed to pending approval',
  selection = null
) => {
  const body = _buildSelectionBody(productIds, selection);
  if (!body) throw new Error('productIds must be a string or array');
  const response = await stFetch(`${API_V2_BASE_URL}/products/bulk-push-to-pending-approval/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ...body, reason }),
  });

  return handleResponse(response);
};

/**
 * Fetch one product-export job's status.
 *
 * The file is delivered by email, so this isn't needed for the normal flow — it
 * exists for surfacing an in-app download link without waiting for the mail.
 */
export const getProductExportJob = async (jobId) => {
  const response = await stFetch(`${API_V2_BASE_URL}/products/export-jobs/${jobId}/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

/**
 * Queue a product CSV export.
 *
 * Every export is queued server-side — small ones too — so each has a
 * ProductExportJob row for logging. The rendered file is uploaded to storage and
 * its link emailed to the requester, so this resolves as soon as the job is
 * queued rather than waiting for or downloading the file.
 *
 * @param {Array<string>} productIds - Product UUIDs (ignored when selection.selectAll)
 * @param {{selectAll: boolean, filters: Object, excludedIds: Array<string>}} [selection]
 * @returns {Promise<{job_id: string, email: string, message: string}>}
 */
export const exportProductsToCSV = async (productIds, selection = null) => {
  const body = selection?.selectAll
    ? {
        select_all: true,
        filters: selection.filters || {},
        excluded_ids: selection.excludedIds || [],
      }
    : { product_ids: productIds };

  const response = await stFetchSlow(`${API_V2_BASE_URL}/products/export-csv/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });

  const data = await handleResponse(response);

  if (!data.job_id) {
    throw new Error(data.error || 'Export could not be queued.');
  }

  return data;
};

/**
 * Export product audit logs to Excel (all products - bulk export)
 * @returns {Promise<void>}
 */
export const exportAdminProductLogs = async () => {
  const response = await stFetchSlow(`${API_V2_BASE_URL}/products/logs/export/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to export product audit logs');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
  link.download = `all_product_logs_${dateStr}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Bulk update products from CSV rows (exported CSV format)
 * @param {Array} rows - Array of product row objects with product_id and sku
 * @returns {Promise<{updated: number, errors: Array}>}
 */
export const bulkUpdateProducts = async (rows) => {
  const response = await stFetch(`${API_V2_BASE_URL}/products/bulk-update/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ rows }),
  });

  return handleResponse(response);
};

/**
 * Get product audit history
 * @param {string} productId - Product UUID
 * @returns {Promise<{audit_history: Array}>}
 */
export const getProductAuditHistory = async (productId) => {
  const response = await stFetch(`${API_V2_BASE_URL}/products/${productId}/audit-history/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  return handleResponse(response);
};

/**
 * Get product review summary for the approval screen.
 * @param {string} productId - Product UUID
 * @returns {Promise<{
 *   missing_fields: Array,
 *   recent_vendor_changes: Array,
 *   changes: {
 *     has_changes: boolean,
 *     reason: string|null,
 *     baseline: {captured_at: string, captured_reason: string, captured_by: object|null}|null,
 *     summary: object,
 *     product: Array,
 *     variants: {added: Array, removed: Array, modified: Array},
 *     media: {added: Array, removed: Array, modified: Array, reordered: {count: number, summary: string, items: Array}},
 *     options: {changed: boolean, added: Array, removed: Array, renamed: Array},
 *   }|null,
 *   editors: Array<{id: string, name: string, email: string, role: string, at: string}>,
 * }>}
 *
 * `changes` is the structured snapshot-vs-current diff — feed it to
 * <ChangesReviewPanel>. `recent_vendor_changes` is the older flat audit replay,
 * kept only as the fallback for products that entered pending before change
 * tracking existed (`changes.reason === 'legacy_pending_no_baseline'`).
 */
export const getProductReviewSummary = async (productId) => {
  const response = await stFetch(`${API_V2_BASE_URL}/products/${productId}/review-summary/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  return handleResponse(response);
};

/**
 * List all vendors
 * @returns {Promise<{vendors: Array}>}
 */
export const listVendors = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== null) {
      queryParams.append(key, params[key]);
    }
  });

  const url = `${API_BASE_URL}/shipturtle/vendors/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await stFetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  return handleResponse(response);
};

/**
 * Search vendors by name (optimized for autocomplete)
 * @param {string} searchTerm - Search term
 * @param {number} limit - Maximum number of results (default: 20)
 * @returns {Promise<{vendors: Array}>}
 */
export const searchVendors = async (searchTerm, limit = 20) => {
  if (!searchTerm || searchTerm.trim().length === 0) {
    return { vendors: [], results: [] };
  }

  const queryParams = new URLSearchParams({
    search: searchTerm.trim(),
    page_size: limit.toString(),
  });

  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/vendors/?${queryParams.toString()}`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    }
  );

  const data = await handleResponse(response);

  return {
    vendors: data.results || data.vendors || [],
    results: data.results || data.vendors || [],
    count: data.count || 0,
  };
};

/**
 * Search customers by name, email, or phone (for order filters)
 * @param {string} searchTerm - Search term (minimum 2 characters)
 * @param {number} limit - Maximum number of results (default: 20)
 * @returns {Promise<{customers: Array}>}
 */
export const searchCustomers = async (searchTerm, limit = 20) => {
  if (!searchTerm || searchTerm.trim().length < 2) {
    return { customers: [] };
  }
  
  const queryParams = new URLSearchParams({
    q: searchTerm.trim(),
  });
  
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/search-customers/?${queryParams.toString()}`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    }
  );
  
  const data = await handleResponse(response);
  
  return {
    customers: data.customers || [],
    success: data.success || false,
  };
};

/**
 * Search order tags by partial match (for order filters)
 * @param {string} searchTerm - Search term (minimum 1 character)
 * @param {number} limit - Maximum number of results (default: 20)
 * @returns {Promise<{tags: Array}>}
 */
export const searchTags = async (searchTerm, limit = 20) => {
  if (!searchTerm || searchTerm.trim().length < 1) {
    return { tags: [] };
  }
  
  const queryParams = new URLSearchParams({
    q: searchTerm.trim(),
  });
  
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/search-tags/?${queryParams.toString()}`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    }
  );
  
  const data = await handleResponse(response);
  
  return {
    tags: data.tags || [],
    success: data.success || false,
  };
};

/**
 * Search categories by name (optimized for autocomplete)
 * @param {string} searchTerm - Search term
 * @param {number} limit - Maximum number of results (default: 20)
 * @returns {Promise<{categories: Array}>}
 */
export const searchCategories = async (searchTerm, limit = 20) => {
  if (!searchTerm || searchTerm.trim().length === 0) {
    return { categories: [] };
  }

  const queryParams = new URLSearchParams({
    search: searchTerm.trim(),
    limit: limit.toString(),
  });

  const response = await stFetch(
    `${API_V2_BASE_URL}/categories/?${queryParams.toString()}`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    }
  );

  const data = await handleResponse(response);

  return {
    categories: data.categories || data.results || [],
  };
};

/**
 * Update a vendor with the new API fields
 * @param {string} vendorId - Vendor UUID
 * @param {Object} vendorData - Updated vendor data
 * @returns {Promise<{vendor: Object}>}
 */
export const updateVendor = async (vendorId, vendorData) => {
  const isFormData = typeof FormData !== 'undefined' && vendorData instanceof FormData;
  // Vendor update endpoint is implemented as multipart in backend (see curl -F usage).
  // Use multipart for all object payloads to avoid parser mismatches.
  const shouldUseMultipart = isFormData || (vendorData && typeof vendorData === 'object');

  const response = await stFetch(`${API_BASE_URL}/shipturtle/vendors/${vendorId}/update/`, {
    method: 'PATCH',
    headers: shouldUseMultipart ? getAuthHeadersMultipart() : getAuthHeaders(),
    body: shouldUseMultipart
      ? (isFormData ? vendorData : buildVendorUpdateFormData(vendorData))
      : JSON.stringify(vendorData),
  });

  return handleResponse(response);
};

/**
 * Update vendor billing & invoice settings
 * @param {string} vendorId - Vendor UUID
 * @param {Object} billingData - Billing settings payload
 * @returns {Promise<Object>}
 */
export const updateVendorBilling = async (vendorId, billingData) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/vendors/${vendorId}/billing/`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(billingData),
  });

  return handleResponse(response);
};

// ============================================
// VENDOR PICKUP ADDRESSES (WAREHOUSES) API
// ============================================

/**
 * List vendor pickup addresses
 * @param {string} vendorId
 * @param {{is_default?: boolean}} [params]
 */
export const listVendorPickupAddresses = async (vendorId, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.is_default === true) queryParams.append('is_default', 'true');
  if (params.is_default === false) queryParams.append('is_default', 'false');

  const url = `${API_BASE_URL}/shipturtle/vendors/${vendorId}/pickup-addresses/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await stFetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  return handleResponse(response);
};

/**
 * Create vendor pickup address
 * POST /vendors/{vendorId}/pickup-addresses/add/
 */
export const createVendorPickupAddress = async (vendorId, payload) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/vendors/${vendorId}/pickup-addresses/add/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  return handleResponse(response);
};

/**
 * Update vendor pickup address
 * PUT/PATCH /vendors/{vendorId}/pickup-addresses/{pickupAddressId}/update/
 */
export const updateVendorPickupAddress = async (vendorId, pickupAddressId, payload, method = 'PATCH') => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/vendors/${vendorId}/pickup-addresses/${pickupAddressId}/update/`,
    {
      method,
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );

  return handleResponse(response);
};

/**
 * Delete vendor pickup address
 * DELETE /vendors/{vendorId}/pickup-addresses/{pickupAddressId}/delete/
 */
export const deleteVendorPickupAddress = async (vendorId, pickupAddressId) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/vendors/${vendorId}/pickup-addresses/${pickupAddressId}/delete/`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }
  );

  return handleResponse(response);
};

// ============================================
// VENDOR CONTACTS API
// ============================================

/**
 * List vendor contacts
 * @param {string} vendorId
 * @param {{contact_type?: string, is_active?: boolean}} [params]
 */
export const listVendorContacts = async (vendorId, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.contact_type) queryParams.append('contact_type', params.contact_type);
  if (params.is_active === true) queryParams.append('is_active', 'true');
  if (params.is_active === false) queryParams.append('is_active', 'false');

  const url = `${API_BASE_URL}/shipturtle/vendors/${vendorId}/contacts/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await stFetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  return handleResponse(response);
};

/**
 * Create vendor contact
 * POST /vendors/{vendorId}/contacts/add/
 */
export const createVendorContact = async (vendorId, payload) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/vendors/${vendorId}/contacts/add/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  return handleResponse(response);
};

/**
 * Update vendor contact
 * PUT/PATCH /vendors/{vendorId}/contacts/{contactId}/update/
 */
export const updateVendorContact = async (vendorId, contactId, payload, method = 'PATCH') => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/vendors/${vendorId}/contacts/${contactId}/update/`,
    {
      method,
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );

  return handleResponse(response);
};

/**
 * Delete vendor contact
 * DELETE /vendors/{vendorId}/contacts/{contactId}/delete/
 */
export const deleteVendorContact = async (vendorId, contactId) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/vendors/${vendorId}/contacts/${contactId}/delete/`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }
  );

  return handleResponse(response);
};

/**
 * Upload vendor logo
 * @param {string} vendorId - Vendor UUID
 * @param {File} file - Logo file
 * @returns {Promise<{logo_url: string}>}
 */
export const uploadVendorLogo = async (vendorId, file) => {
  const formData = new FormData();
  formData.append('logo', file);

  const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
  const response = await stFetch(`${API_BASE_URL}/shipturtle/vendors/${vendorId}/upload-logo/`, {
    method: 'POST',
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
    },
    body: formData,
  });

  return handleResponse(response);
};

/**
 * Upload vendor documents
 * @param {string} vendorId - Vendor UUID
 * @param {File} file - Document file
 * @returns {Promise<{document_url: string}>}
 */
export const uploadVendorDocument = async (vendorId, file) => {
  const formData = new FormData();
  formData.append('document', file);

  const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
  const response = await stFetch(`${API_BASE_URL}/shipturtle/vendors/${vendorId}/upload-document/`, {
    method: 'POST',
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
    },
    body: formData,
  });

  return handleResponse(response);
};

/**
 * Bulk upload products via CSV
 * @param {FormData} formData - FormData containing the CSV file
 * @param {Object} options - Optional query parameters (validate_only, initial_status, mode)
 * @returns {Promise<{
 *   success: boolean,
 *   message: string,
 *   results: {
 *     total_rows: number,
 *     processed_products: number,
 *     processed_variants: number,
 *     errors: string[],
 *     warnings: string[],
 *     created_products: string[],
 *     updated_products: string[],
 *     success_rate: number,
 *     validate_only: boolean,
 *     validation_results: Array<BulkRowValidation>,
 *     validation_summary?: {
 *       total_rows: number,
 *       error_count: number,
 *       warning_count: number,
 *       valid_count: number,
 *       validation_results_returned: number,
 *       note: string
 *     }
 *   }
 * }>}
 * @description For large batches (>1000 rows), validation_results is limited to first 1000 error/warning rows,
 * and validation_summary is provided with full statistics. For small batches (≤1000 rows),
 * all validation_results are returned and validation_summary is not present.
 */
export const bulkUploadProducts = async (formData, options = {}) => {
  const token = localStorage.getItem('token');

  // Build query string from options. Catalog status is per-row in the sheet, and
  // approval state is always PENDING server-side — neither is a query param.
  const queryParams = new URLSearchParams();
  if (options.validate_only) queryParams.append('validate_only', 'true');
  if (options.mode) queryParams.append('mode', options.mode);
  if (options.vendor_id) queryParams.append('vendor_id', options.vendor_id);

  const queryString = queryParams.toString();
  const url = `${API_V2_BASE_URL}/products/bulk-upload/${queryString ? '?' + queryString : ''}`;

  const response = await stFetch(url, {
    method: 'POST',
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
    },
    body: formData,
  });

  return handleResponse(response);
};

/**
 * Validate a single bulk upload row
 * @param {Object} rowData - Row data to validate
 * @returns {Promise<{valid: boolean, errors: Array}>}
 */
export const validateBulkUploadRow = async (rowData, mode = 'add') => {
  const response = await stFetch(`${API_V2_BASE_URL}/products/validate-row/?mode=${mode}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(rowData),
  });

  return handleResponse(response);
};

/**
 * Download the bulk-upload template (.xlsx).
 *
 * Generated server-side so the `status` column can carry a real Excel dropdown —
 * a CSV built in the browser cannot express data validation.
 *
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
export const downloadBulkUploadTemplate = async () => {
  const response = await stFetchSlow(`${API_V2_BASE_URL}/products/bulk-upload/template/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    if (response.status === 401) {
      handleRawFetch401();
      throw new Error('Session expired');
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to download the template');
  }

  return { blob: await response.blob(), filename: 'product_upload_template.xlsx' };
};

/**
 * Import bulk rows
 * @param {Array} rows - Array of validated rows to import
 * @param {Object} options - Optional parameters (vendor_id)
 * @returns {Promise<{imported_count: number, failed_count: number}>}
 */
export const importBulkRows = async (rows, options = {}) => {
  const token = localStorage.getItem('token');

  // Build query string from options. Catalog status is per-row in the sheet, and
  // approval state is always PENDING server-side — neither is a query param.
  const queryParams = new URLSearchParams();
  if (options.vendor_id) queryParams.append('vendor_id', options.vendor_id);

  const queryString = queryParams.toString();
  const url = `${API_V2_BASE_URL}/products/bulk-upload/${queryString ? '?' + queryString : ''}`;

  const response = await stFetch(url, {
    method: 'POST',
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ rows }),
  });

  return handleResponse(response);
};

// ============================================
// EXPORTS API
// ============================================

/**
 * Create an export job or download immediately when ready
 * Supports two modes:
 * 1. orderIds array mode: when selectAll=false, uses orderIds array
 * 2. select_all mode: when selectAll=true, uses filters (ignores orderIds)
 * 
 * @param {string} type - 'shipping_labels'|'invoices'|'orders_csv'
 * @param {string} format - 'zip'|'pdf'|'csv'
 * @param {Array<string>} orderIds - Array of order IDs (used when selectAll=false)
 * @param {boolean} selectAll - If true, export all matching filters (default: false)
 * @param {Object} filters - Filters to apply (used when selectAll=true)
 * @param {Object} sort - Sort configuration {field: string, direction: 'asc'|'desc'}
 * @returns {Promise<Object>}
 */
export const createExport = async (type, format, orderIds = [], selectAll = false, filters = {}, sort = {}, columns = null, hideCustomerDetails = false) => {
  // Build request body based on selectAll flag
  const payload = {
    type,
    format,
    sort,
    select_all: selectAll,  // Always include for consistency with other APIs
    hide_customer_details: hideCustomerDetails,
  };

  // Include custom column selection for CSV exports
  if (Array.isArray(columns) && columns.length > 0) {
    payload.columns = columns;
  }

  if (selectAll) {
    // Select all mode: use filters, ignore orderIds
    payload.filters = filters;
    // Don't include orderIds when using select_all
  } else {
    // Order IDs mode: include orderIds if provided
    if (Array.isArray(orderIds) && orderIds.length > 0) {
      payload.orderIds = orderIds;
    }
    // Can still include filters for additional filtering even with specific orderIds
    if (filters && Object.keys(filters).length > 0) {
      payload.filters = filters;
    }
  }

  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/exports/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

/**
* Get export job status
* @param {string} jobId
* @returns {Promise<Object>}
*/
export const getExportStatus = async (jobId) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/exports/${jobId}/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  return handleResponse(response);
};

/**
 * Export columns available to the current user's role.
 *
 * Single source of truth for the column picker: admins and vendors get
 * different column sets, and the backend drops any column the role isn't
 * entitled to. Fetching avoids the frontend drifting from the backend lists.
 */
export const getExportColumns = async () => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/exports/columns/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  return handleResponse(response);
};

// ============================================
// SHOPIFY VENDOR INTEGRATION API
// ============================================
/**
 * Connect a Shopify store for the current vendor
 * @param {Object} payload
 * @param {string} payload.shop_name - Shopify shop subdomain (e.g. "your-shop")
 * @param {string} payload.auth_token - Shopify Admin API access token
 */
export const connectShopifyVendor = async (payload) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/vendors/connect-shopify/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  return handleResponse(response);
};

/**
 * Get current Shopify configuration for the vendor
 * @param {string} [vendorId]
 * @returns {Promise<Object>}
 */
export const getShopifyVendorConfig = async (vendorId = null) => {
  const url = vendorId 
    ? `${API_BASE_URL}/shipturtle/vendors/${vendorId}/shopify-config/`
    : `${API_BASE_URL}/shipturtle/vendors/shopify-config/`;
    
  const response = await stFetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  return handleResponse(response);
};

/**
 * Trigger a Shopify product sync for the connected store
 * @param {Object} payload
 * @param {string} payload.shopify_auth_token - Shopify Admin API access token
 * @returns {Promise<Object>}
 */
export const syncShopifyVendor = async (payload) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/vendors/sync-shopify/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  return handleResponse(response);
};


export const getShopifyStoreProducts = async (vendorId, filters = {}) => {
  const params = new URLSearchParams();
  
  // Add optional filter parameters
  if (filters.vendor_shopify_status) {
    params.append('vendor_shopify_status', filters.vendor_shopify_status);
  }
  if (filters.suspire_shopify_status) {
    params.append('suspire_shopify_status', filters.suspire_shopify_status);
  }
  
  const queryString = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/shipturtle/v2/vendors/${vendorId}/connected-store/products/${queryString}`;

  const response = await stFetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const getInternalStoreProducts = async (vendorId, query = '', shopifyId = null) => {
  const params = new URLSearchParams();
  if (query) params.append('q', query);
  if (shopifyId) params.append('shopify_id', shopifyId);

  const queryString = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/shipturtle/v2/vendors/${vendorId}/connected-store/products/internal/${queryString}`;

  const response = await stFetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const linkExistingProduct = async (vendorId, payload) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/v2/vendors/${vendorId}/connected-store/products/link-existing/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};


export const pushProductToCatalog = async (vendorId, payload) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/v2/vendors/${vendorId}/connected-store/products/push/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

export const bulkPushProductsToCatalog = async (vendorId, payload) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/v2/vendors/${vendorId}/connected-store/products/bulk-push/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};



export const unmapProduct = async (vendorId, payload) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/v2/vendors/${vendorId}/connected-store/products/unmap/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

export const bulkUnmapProducts = async (vendorId, payload) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/v2/vendors/${vendorId}/connected-store/products/bulk-unmap/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

export const fixShopifyDrafts = async (vendorId) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/v2/vendors/${vendorId}/connected-store/fix-drafts/`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};


export const updateProductMapping = async (vendorId, mappingId, payload) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/v2/vendors/${vendorId}/connected-store/products/${mappingId}/`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};


export const registerWebhook = async (vendorId) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/v2/vendors/${vendorId}/connected-store/register-webhook/`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};


export const deregisterWebhook = async (vendorId) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/v2/vendors/${vendorId}/connected-store/deregister-webhook/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const deregisterWebhookByType = async (vendorId, type) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/v2/vendors/${vendorId}/connected-store/deregister-webhook/?type=${type}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};


export const getActiveWebhooks = async (vendorId) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/v2/vendors/${vendorId}/connected-store/webhooks/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
    return handleResponse(response);
};
// ============================================
// NOTES API
// ============================================

/**
 * List notes for an entity
 * @param {string} entityType - 'order'|'customer'|'vendor'
 * @param {string} entityId - UUID of the entity
 */
export const listNotes = async (entityType, entityId) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/notes/${entityType}/${entityId}/`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  return handleResponse(response);
};

/**
 * Create a note for an entity
 * @param {string} entityType
 * @param {string} entityId
 * @param {{ content: string }} payload
 */
export const createNote = async (entityType, entityId, payload) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/notes/${entityType}/${entityId}/`,
    { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload) }
  );
  return handleResponse(response);
};

/**
 * Update a note
 * @param {string} noteId
 * @param {{ content?: string }} payload
 */
export const updateNote = async (noteId, payload) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/notes/${noteId}/`,
    { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(payload) }
  );
  return handleResponse(response);
};

/**
 * Delete a note
 * @param {string} noteId
 */
export const deleteNote = async (noteId) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/notes/${noteId}/`,
    { method: 'DELETE', headers: getAuthHeaders() }
  );
  return handleResponse(response);
};

/**
 * Fetch customer-visible notes for an order (used by storefront)
 * @param {string} orderId
 */
export const getCustomerOrderNotes = async (orderId) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/notes/customer/order/${orderId}/`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  return handleResponse(response);
};

const shipturtleApi = {
  listProducts,
  getProductDetail,
  createProduct,
  updateProduct,
  deleteProduct,
  listCategories,
  getCategoryHierarchy,
  listOrders,
  getOrderDetail,
  approveProduct,
  rejectProduct,
  requestChangesProduct,
  bulkApproveProducts,
  moveToDraft,
  moveToActive,
  moveToUnlisted,
  bulkMoveToDraft,
  bulkDeleteProducts,
  pushToPendingApproval,
  bulkPushToPendingApproval,
  getProductAuditHistory,
  getProductReviewSummary,
  listVendors,
  searchVendors,
  searchCategories,
  updateVendor,
  updateVendorBilling,
  listVendorPickupAddresses,
  createVendorPickupAddress,
  updateVendorPickupAddress,
  deleteVendorPickupAddress,
  uploadVendorLogo,
  uploadVendorDocument,
  bulkUploadProducts,
  validateBulkUploadRow,
  importBulkRows,
  createExport,
  getExportStatus,
  connectShopifyVendor,
  getShopifyVendorConfig,
  syncShopifyVendor,
  getShopifyStoreProducts,
  getInternalStoreProducts,
  linkExistingProduct,
  pushProductToCatalog,
  bulkPushProductsToCatalog,
  unmapProduct,
  updateProductMapping,
  registerWebhook,
  deregisterWebhook,
  deregisterWebhookByType,
  getActiveWebhooks,
  bulkUnmapProducts,
  getSyncFieldFilters,
  updateSyncFieldFilters,
  listNotes,
  createNote,
  updateNote,
  deleteNote,
  getCustomerOrderNotes,
  updateProductOptions,
  updateVariantField,
  assignVariantImage,
  reorderProductImages,
  getTerminalBanners,
  getFAQDocuments,
};

export default shipturtleApi;

// ── Terminal Banners & FAQ ────────────────────────────────────────────────────

export async function getFAQDocuments() {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/faq/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function getTerminalBanners() {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/terminal-banners/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}


// ── Vendor export & bulk update ───────────────────────────────────────────────

const VENDORS_BASE_URL = `${API_BASE_URL}/shipturtle/vendors`;

/**
 * Read an xlsx download, or surface the server's error message.
 *
 * Success is a binary blob but failures come back as JSON, so the branch has to
 * happen before the body is read — consuming the stream the wrong way is not
 * recoverable.
 */
const readFileResponse = async (response, fallbackName) => {
  if (!response.ok) {
    if (response.status === 401) {
      handleRawFetch401();
      throw new Error('Session expired');
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Download failed');
  }

  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  return {
    blob: await response.blob(),
    filename: match ? match[1] : fallbackName,
    count: Number(response.headers.get('X-Vendor-Count')) || null,
  };
};

/**
 * Export vendors to .xlsx. Synchronous — resolves with the file itself.
 *
 * Pass either an explicit `vendorIds` list or `selectAll` plus the list's current
 * filters; the backend re-resolves a select-all against those filters so the file
 * matches what the admin was actually looking at.
 *
 * @returns {Promise<{blob: Blob, filename: string, count: number|null}>}
 */
export const exportVendors = async ({
  preset = 'core',
  vendorIds = [],
  selectAll = false,
  filters = {},
  excludedIds = [],
} = {}) => {
  const body = selectAll
    ? { preset, select_all: true, filters, excluded_ids: excludedIds }
    : { preset, vendor_ids: vendorIds };

  const response = await stFetchSlow(`${VENDORS_BASE_URL}/export/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });

  return readFileResponse(response, `vendors-${preset}.xlsx`);
};

/** Column metadata for the export picker and preview table. */
export const getVendorExportColumns = async (preset = 'full') => {
  const response = await stFetch(`${VENDORS_BASE_URL}/export/columns/?preset=${preset}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

/** Download an empty core-shaped sheet. */
export const downloadVendorBulkUpdateTemplate = async () => {
  const response = await stFetch(`${VENDORS_BASE_URL}/bulk-update/template/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return readFileResponse(response, 'vendor_bulk_update_template.xlsx');
};

/**
 * Dry run: upload a sheet and get the per-row diff back. Writes nothing.
 *
 * @returns {Promise<{summary: object, results: Array, results_truncated: boolean}>}
 *   Each result carries `changes` (an ordered [{field, label, old, new, kind}]),
 *   `errors` ({field: message}), `warnings`, and a `status` of
 *   valid | unchanged | error.
 */
export const validateVendorBulkUpdate = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await stFetch(`${VENDORS_BASE_URL}/bulk-update/?validate_only=true`, {
    method: 'POST',
    headers: getAuthHeadersMultipart(),
    body: formData,
  });
  return handleResponse(response);
};

/** Revalidate a single row edited inline in the preview. */
export const validateVendorBulkRow = async (row, rowNumber = 2) => {
  const response = await stFetch(`${VENDORS_BASE_URL}/bulk-update/validate-row/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ row, row_number: rowNumber }),
  });
  return handleResponse(response);
};

/**
 * Commit the previewed rows. The backend recomputes the diff against fresh DB
 * state, so anything changed by someone else since the preview is reported
 * accurately rather than applied blind.
 */
export const commitVendorBulkUpdate = async (rows, { includeUnchanged = false } = {}) => {
  const query = includeUnchanged ? '?include_unchanged=true' : '';
  const response = await stFetch(`${VENDORS_BASE_URL}/bulk-update/${query}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ rows }),
  });
  return handleResponse(response);
};

/** Save a downloaded blob to the user's disk. */
export const saveBlobAs = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Shipment labels — the terminal Forward/Reverse screen
// ---------------------------------------------------------------------------

/**
 * Find orders to label, by order number.
 * Nothing is pre-fetched: the operator types an order number and we look it up.
 */
export const searchOrdersForLabel = async (query, limit = 10) => {
  // Dedicated endpoint: the orders list is a tab view and requires a status,
  // but a label can be raised against an order in any state.
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/label-search/?${params}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

/**
 * Everything the label form needs for one order, in a single call: line items
 * with their remaining un-shipped quantity for this leg, both parties
 * pre-filled in the right direction, and the vendor's warehouses.
 *
 * @param {'forward'|'reverse'} type
 */
export const getLabelContext = async (orderId, type = 'forward') => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/${orderId}/label-context/?type=${type}`,
    { headers: getAuthHeaders() }
  );
  return handleResponse(response);
};

/**
 * Serviceable couriers and prices for one leg.
 *
 * Distinct from the Orders-tab cost calculator, which derives the lane from the
 * order as pickup -> shipping. A reverse leg runs the other way, and either
 * address may have been edited on the form, so the lane is passed explicitly.
 */
export const getLabelCourierQuotes = async (orderId, payload) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/${orderId}/label-couriers/`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );
  return handleResponse(response);
};

/**
 * Create a forward or reverse label for the selected line items.
 *
 * Distinct from the Orders-tab label action, which ships a whole new order.
 * `payload.type` picks the leg; `charge_bearer` records who absorbs the cost.
 */
/**
 * Merge one shipment's label and manifest into a single PDF and return its URL.
 *
 * Addressed by shipment, not by order: the order-scoped variant resolves the
 * order's *active* shipment, which a reverse leg never is.
 */
export const downloadShipmentDocuments = async (shipmentId) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/shipments/${shipmentId}/download-combined/`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    }
  );
  return handleResponse(response);
};

export const generateShipmentLabel = async (orderId, payload) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/${orderId}/generate-shipment-label/`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );
  return handleResponse(response);
};

// Admin-only list of every return/replacement label. Rows are shipments rather
// than orders, so this is its own endpoint rather than a filter on the orders list.
export const fetchExceptionLabels = async (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/exception-labels/?${query}`,
    { headers: getAuthHeaders() }
  );
  return handleResponse(response);
};

// Cancels one exception label. Deliberately shipment-scoped: the order-scoped
// cancel rewrites Order.status, which would un-deliver the order this label
// belongs to.
export const cancelExceptionLabel = async (shipmentId, cancelReason) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/shipments/${shipmentId}/cancel/`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ cancel_reason: cancelReason }),
    }
  );
  return handleResponse(response);
};

// Everything needed to re-raise a near-identical label, already shaped as a
// generate-shipment-label body.
export const fetchExceptionLabelCloneContext = async (shipmentId) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/shipments/${shipmentId}/clone-context/`,
    { headers: getAuthHeaders() }
  );
  return handleResponse(response);
};

// ---------------------------------------------------------------------------
// Manual (off-Shopify) orders — the terminal's third Orders tab
// ---------------------------------------------------------------------------
//
// Orders placed outside Shopify, keyed in by hand. They live in their own table
// with no link to a terminal Order, so every call here is its own endpoint
// rather than a filter on the orders list.

/** List manual orders. Supports q, status, payment_mode, charge_bearer, dates. */
export const listManualOrders = async (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    // Repeated keys for multi-select filters, matching the backend's getlist().
    if (Array.isArray(value)) value.forEach((v) => query.append(key, String(v)));
    else query.append(key, String(value));
  });
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/manual/?${query}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const getManualOrder = async (id) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/manual/${id}/`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

/** Create a manual order as a DRAFT. The label is a separate, later step. */
export const createManualOrder = async (payload) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/manual/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

/**
 * Save a manual order and raise its label in one step.
 *
 * The server saves the order only if Prozo issues the AWB: any refusal rolls
 * everything back, so a failed attempt leaves nothing behind and can simply be
 * resubmitted once corrected.
 */
export const createManualOrderWithLabel = async (payload) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/manual/create-and-label/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

/** Edit a manual order. Server-side this is refused once a label exists. */
export const updateManualOrder = async (id, payload) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/manual/${id}/`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

/**
 * Quote a typed lane.
 *
 * Takes the lane rather than an order id, because the wizard quotes *before*
 * the order exists — the operator needs the price to pick a channel, and the
 * channel is part of what gets saved.
 */
export const getManualOrderCouriers = async (payload) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/manual/couriers/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

/**
 * Search the address book that prefills the wizard: `kind` is 'customer' or
 * 'vendor' (one entry per vendor warehouse). Read-only — a pick copies values
 * into the form and nothing links the order to the entry.
 */
export const searchManualOrderAddressBook = async ({ kind, q = '' }, { signal } = {}) => {
  const query = new URLSearchParams({ kind, q });
  // The caller's signal belongs in the resilience options, not in the fetch
  // options: stFetch builds its own controller for the timeout and would
  // overwrite a signal passed alongside the headers, so a cancelled keystroke
  // search would keep running.
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/manual/address-book/?${query}`,
    { headers: getAuthHeaders() },
    { signal }
  );
  return handleResponse(response);
};

export const generateManualOrderLabel = async (id, payload = {}) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/manual/${id}/generate-label/`,
    { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload) }
  );
  return handleResponse(response);
};

/** Cancel at Prozo and soft-cancel locally. The row is kept, never deleted. */
export const cancelManualOrder = async (id, reason = '') => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/manual/${id}/cancel/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ reason }),
  });
  return handleResponse(response);
};

/**
 * Everything the wizard needs to open pre-filled from an existing order.
 *
 * A prefill, not a saved row: nothing is written until the operator reviews the
 * steps and submits, so an address can be corrected, the lane re-quoted and a
 * new reason given before an AWB is spent.
 */
export const fetchManualOrderCloneContext = async (id) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/manual/${id}/clone-context/`,
    { headers: getAuthHeaders() }
  );
  return handleResponse(response);
};

/** Label and manifest URLs, re-fetching either if it went missing. */
export const getManualOrderDocuments = async (id) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/manual/${id}/documents/`,
    { headers: getAuthHeaders() }
  );
  return handleResponse(response);
};

// ---------------------------------------------------------------------------
// B2B (bulk/institutional) orders
//
// A two-stage flow: the B2B team raises the order with its vendors, products
// and pickup/delivery details, then ops attaches the shipment once a PTL
// consignment is booked. Standalone rows — no link to a terminal Order, vendor
// or brand — so every call here is its own endpoint.
// ---------------------------------------------------------------------------

/** List B2B orders. Supports q, vendor, mode, status, dates, has_shipment. */
export const listB2BOrders = async (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) value.forEach((v) => query.append(key, String(v)));
    else query.append(key, String(value));
  });
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/b2b/?${query}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const getB2BOrder = async (id) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/b2b/${id}/`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

/** Stage 1 — create the order with its nested vendors and products. */
export const createB2BOrder = async (payload) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/b2b/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

/** Edit any field. Every change is recorded in the order's history. */
export const updateB2BOrder = async (id, payload) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/b2b/${id}/`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

/**
 * Delete an order.
 *
 * Soft server-side: the row and its children are kept and simply stop being
 * returned, so this is reversible by an admin. The optional reason is recorded
 * in the order's change log.
 */
export const deleteB2BOrder = async (id, reason = '') => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/b2b/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    body: JSON.stringify({ reason }),
  });
  return handleResponse(response);
};

/** Stage 2 — ops records a booked consignment. AWB, link and shipper required. */
export const addB2BShipment = async (orderId, payload) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/b2b/${orderId}/shipments/`,
    { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload) }
  );
  return handleResponse(response);
};

export const updateB2BShipment = async (shipmentId, payload) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/b2b/shipments/${shipmentId}/`,
    { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(payload) }
  );
  return handleResponse(response);
};

export const deleteB2BShipment = async (shipmentId) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/b2b/shipments/${shipmentId}/`,
    { method: 'DELETE', headers: getAuthHeaders() }
  );
  return handleResponse(response);
};

/**
 * Upload or replace the prescription PDF on a "Prescription"-tagged order.
 * Labels cannot be generated for such an order until one is on file.
 * `acknowledged` must be true: the server refuses a submission without it.
 */
export const uploadOrderPrescription = async (orderId, file, acknowledged) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('acknowledged', acknowledged ? 'true' : 'false');
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/${orderId}/prescription/`, {
    method: 'POST',
    // No Content-Type — the browser sets the multipart boundary.
    headers: getAuthHeadersMultipart(),
    body: formData,
  });
  return handleResponse(response);
};

/**
 * Add or replace the prescription as a link instead of a PDF (e.g. one issued
 * on a telemedicine platform). Replaces any earlier PDF.
 */
export const saveOrderPrescriptionLink = async (orderId, link, acknowledged) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/${orderId}/prescription/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ link, acknowledged: Boolean(acknowledged) }),
  });
  return handleResponse(response);
};

/**
 * The order's prescription metadata plus a short-lived `download_url` (PDF)
 * or the `link`.
 * Fetch it right before opening the file — the URL expires in minutes.
 */
export const getOrderPrescription = async (orderId) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/${orderId}/prescription/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

/**
 * Upload the invoice PDF.
 *
 * Its own endpoint rather than the shared /core/s3/upload/, which requires the
 * user to have a brand — internal admins do not have one, so that endpoint
 * rejects every user of this screen.
 */
export const uploadB2BInvoiceDocument = async (orderId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/b2b/${orderId}/invoice-document/`,
    {
      method: 'POST',
      // No Content-Type — the browser sets the multipart boundary.
      headers: getAuthHeadersMultipart(),
      body: formData,
    }
  );
  return handleResponse(response);
};

/**
 * Upload the e-way bill PDF. Optional on every order — whether one is needed is
 * the admin's call — so it can also be removed again.
 */
export const uploadB2BEwayBillDocument = async (orderId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/b2b/${orderId}/eway-bill-document/`,
    { method: 'POST', headers: getAuthHeadersMultipart(), body: formData }
  );
  return handleResponse(response);
};

export const deleteB2BEwayBillDocument = async (orderId) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/b2b/${orderId}/eway-bill-document/`,
    { method: 'DELETE', headers: getAuthHeaders() }
  );
  return handleResponse(response);
};

/**
 * Attach additional documents, each with its own name.
 *
 * `items` is [{ name, file }]. Sent as repeated `names` / `files` fields paired
 * by position; the server checks every file before storing any.
 */
export const uploadB2BOrderDocuments = async (orderId, items) => {
  const formData = new FormData();
  items.forEach(({ name, file }) => {
    formData.append('names', name);
    formData.append('files', file);
  });
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/b2b/${orderId}/documents/`,
    { method: 'POST', headers: getAuthHeadersMultipart(), body: formData }
  );
  return handleResponse(response);
};

export const deleteB2BOrderDocument = async (documentId) => {
  const response = await stFetch(
    `${API_BASE_URL}/shipturtle/orders/b2b/documents/${documentId}/`,
    { method: 'DELETE', headers: getAuthHeaders() }
  );
  return handleResponse(response);
};

/**
 * Download the current view as CSV.
 *
 * Returned as a blob rather than a job id: a B2B list is hundreds of rows at
 * most, so the server streams it straight back and the browser saves it.
 */
export const exportB2BOrders = async (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) value.forEach((v) => query.append(key, String(v)));
    else query.append(key, String(value));
  });
  const response = await stFetchSlow(`${API_BASE_URL}/shipturtle/orders/b2b/export/?${query}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error('Could not export these orders.');
  }
  return response.blob();
};

/** The order's change log. */
export const getB2BOrderHistory = async (id) => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/b2b/${id}/history/`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

/** The number the next B2B order will get — a preview, not an allocation. */
export const getNextB2BOrderNumber = async () => {
  const response = await stFetch(`${API_BASE_URL}/shipturtle/orders/b2b/next-number/`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};
